import { memo, ReactElement, useMemo } from 'react';
import { Button, Flex, Space, Tag, Tooltip, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { ExclamationCircleOutlined, UngroupOutlined, GroupOutlined } from '@ant-design/icons';
import { Table } from '@ohpen/enso-ui';

import productStyles from '../../products/styles.module.css';
import { TableHeaderTitleProps } from '../../products/interfaces';
import { SequentialCodeControl } from './SequentialCodeControl';

const { Text } = Typography;

export const TableHeaderTitle = memo(
  ({
    columnKey,
    title,
    editAll,
    disableEditAllKeys,
    columnsErrorsCount,
    validationFeedback,
    handleChangeEditAll,
    bulkAdjustConfig,
    sequentialCodeConfig,
  }: TableHeaderTitleProps) => {
    const { t } = useTranslation();

    const ErrorsTag = useMemo((): ReactElement | null => {
      if (!validationFeedback) return null;

      const count = columnsErrorsCount[columnKey];

      return count ? (
        <Tag color="red" icon={<ExclamationCircleOutlined />}>
          {count}
        </Tag>
      ) : null;
    }, [columnsErrorsCount, columnKey, validationFeedback]);

    if (!columnKey || columnKey === 'actions') return <Text strong>{title}</Text>;

    return (
      <Flex justify="space-between" align="center" gap="middle">
        <Text strong>{title}</Text>
        <Space size="small">
          {bulkAdjustConfig && (
            <Table.BulkAdjustControl
              {...bulkAdjustConfig}
              placeholder={bulkAdjustConfig.placeholder ? t(bulkAdjustConfig.placeholder) : undefined}
              dataIndex={columnKey}
              disabled={editAll}
            />
          )}
          {sequentialCodeConfig && <SequentialCodeControl {...sequentialCodeConfig} />}
          <Tooltip title={t(editAll ? 'global.labels.disableEditAllColumn' : 'global.labels.enableEditAllColumn')} placement="top">
            <Button
              type="text"
              icon={editAll ? <GroupOutlined className={productStyles.lockIcon} /> : <UngroupOutlined />}
              onClick={() => handleChangeEditAll(columnKey)}
              disabled={disableEditAllKeys.includes(columnKey)}
            />
          </Tooltip>
          {ErrorsTag}
        </Space>
      </Flex>
    );
  },
);
